/**
 * Draws the Windows taskbar overlay badge.
 *
 * Windows has no numeric taskbar badge — the platform primitive is a 16×16
 * overlay IMAGE. Electron's `nativeImage` cannot render text, so the number is
 * drawn here, in the renderer, where a canvas already exists. Costs nothing
 * until the count actually changes.
 */

const SIZE = 32;
const MAX = 99;

let lastKey = '';
let lastUrl: string | null = null;

function label(count: number): string {
  return count > MAX ? `${MAX}+` : String(count);
}

/**
 * Returns a PNG data URL for `count`, or null when there is nothing to show.
 * Memoised on the count so a re-render never re-rasterises.
 */
export function badgeOverlayDataUrl(count: number): string | null {
  if (count <= 0) return null;
  const text = label(count);
  if (text === lastKey && lastUrl !== null) return lastUrl;

  const canvas = document.createElement('canvas');
  canvas.width = SIZE;
  canvas.height = SIZE;
  const context = canvas.getContext('2d');
  if (context === null) return null;

  context.beginPath();
  context.arc(SIZE / 2, SIZE / 2, SIZE / 2 - 1, 0, Math.PI * 2);
  context.fillStyle = '#EF4444';
  context.fill();
  context.lineWidth = 2;
  context.strokeStyle = 'rgba(255,255,255,0.9)';
  context.stroke();

  /* Shrink the type for a three-glyph label so "99+" still fits the disc. */
  context.fillStyle = '#FFFFFF';
  context.font = `600 ${text.length > 2 ? 15 : 19}px system-ui, sans-serif`;
  context.textAlign = 'center';
  context.textBaseline = 'middle';
  context.fillText(text, SIZE / 2, SIZE / 2 + 1);

  lastKey = text;
  lastUrl = canvas.toDataURL('image/png');
  return lastUrl;
}
